import { useEffect, useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell, Legend,
} from "recharts";
import api from "@/lib/api";
import { RM, RMcompact } from "@/lib/format";
import { Layout } from "@/components/Layout";

const PALETTE = ["#EF4444", "#F59E0B", "#10B981", "#3B82F6", "#8B5CF6", "#EC4899"];

const tooltipStyle = {
  contentStyle: { background: "#FFFFFF", border: "1px solid #E2E5EA", borderRadius: 6, color: "#0F172A" },
  labelStyle: { color: "#6B7280" },
  itemStyle: { color: "#0F172A" },
  formatter: (v) => RM(v),
};

const Panel = ({ title, children, sub }) => (
  <div className="border border-[#E2E5EA] bg-[#FFFFFF] p-6">
    <h3 className="font-heading text-lg font-semibold text-[#0F172A]">{title}</h3>
    {sub && <p className="mt-0.5 text-sm text-[#9CA3AF]">{sub}</p>}
    <div className="mt-6">{children}</div>
  </div>
);

export default function Reports() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/reports/analytics").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  if (!data)
    return <Layout title="Reports & Analytics"><div className="h-64 animate-pulse rounded-md bg-[#FFFFFF]" /></Layout>;

  const hasTrend = data.trend.length > 0;

  return (
    <Layout title="Reports & Analytics" subtitle="Payroll cost trends and statutory breakdown">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Panel title="Payroll Cost Trend" sub="Gross vs net across processed runs">
          {hasTrend ? (
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={data.trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E5EA" />
                <XAxis dataKey="period" stroke="#6B7280" fontSize={12} />
                <YAxis stroke="#6B7280" fontSize={12} tickFormatter={RMcompact} width={70} />
                <Tooltip {...tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: 12, color: "#9CA3AF" }} />
                <Line type="monotone" dataKey="gross" name="Gross" stroke="#EF4444" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="net" name="Net" stroke="#F59E0B" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex h-[280px] items-center justify-center text-sm text-[#6B7280]">No processed runs yet.</div>
          )}
        </Panel>

        <Panel title="Statutory Contributions" sub="Composition from the latest run / estimate">
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={data.statutory} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={95} innerRadius={55} paddingAngle={2}>
                {data.statutory.map((e, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} stroke="#F4F5F7" />)}
              </Pie>
              <Tooltip {...tooltipStyle} />
              <Legend wrapperStyle={{ fontSize: 12, color: "#9CA3AF" }} />
            </PieChart>
          </ResponsiveContainer>
        </Panel>

        <Panel title="Cost by Department" sub="Monthly gross by department (active employees)">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data.department} layout="vertical" margin={{ left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E5EA" horizontal={false} />
              <XAxis type="number" stroke="#6B7280" fontSize={12} tickFormatter={RMcompact} />
              <YAxis type="category" dataKey="department" stroke="#6B7280" fontSize={12} width={110} />
              <Tooltip {...tooltipStyle} cursor={{ fill: "rgba(245,158,11,0.06)" }} />
              <Bar dataKey="gross" name="Gross" radius={[0, 4, 4, 0]}>
                {data.department.map((e, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Panel>

        <Panel title="Department Headcount" sub="Employees per department">
          <div className="space-y-3">
            {data.department.map((d, i) => (
              <div key={d.department}>
                <div className="mb-1 flex items-center justify-between text-sm">
                  <span className="text-[#9CA3AF]">{d.department}</span>
                  <span className="tabular-nums text-[#0F172A]">{d.headcount} · {RM(d.gross)}</span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-[#EEF1F5]">
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${(d.headcount / Math.max(...data.department.map((x) => x.headcount))) * 100}%`,
                      backgroundColor: PALETTE[i % PALETTE.length],
                    }}
                  />
                </div>
              </div>
            ))}
            {data.department.length === 0 && <p className="text-sm text-[#6B7280]">No department data.</p>}
          </div>
        </Panel>
      </div>
    </Layout>
  );
}
